import { memo, useCallback, useMemo, useState, useEffect } from "react";
import { X, Plus, AlertTriangle } from "lucide-react";

// ─── Utilitaires ───

const extractAnswerWords = (phrase) => {
    const matches = [];
    const re = /\{\{([^}]+)\}\}/g;
    let m;
    while ((m = re.exec(phrase)) !== null) {
        matches.push(m[1].trim());
    }
    return matches;
};

const normalize = (w) => w.trim().toLowerCase();

const validateTexteTrou = (phrase, distracteurs, mode) => {
    const errors = [];
    const answerWords = extractAnswerWords(phrase);

    if (answerWords.length === 0) {
        errors.push('Ajoutez au moins un trou avec {{mot}} dans la phrase.');
        return errors;
    }

    const openCount = (phrase.match(/\{\{/g) || []).length;
    const closeCount = (phrase.match(/\}\}/g) || []).length;
    if (openCount !== closeCount) {
        errors.push('Accolades mal fermées — vérifiez que chaque {{ a son }}.');
    }

    const normalizedAnswers = answerWords.map(normalize);
    if (new Set(normalizedAnswers).size < normalizedAnswers.length) {
        errors.push('Un même mot-réponse apparaît plusieurs fois dans la phrase.');
    }

    const normalizedDistracteurs = distracteurs.map(normalize);
    if (new Set(normalizedDistracteurs).size < normalizedDistracteurs.length) {
        errors.push('Un même distracteur apparaît plusieurs fois.');
    }

    const answersSet = new Set(normalizedAnswers);
    const conflicting = distracteurs.filter(d => answersSet.has(normalize(d)));
    if (conflicting.length > 0) {
        errors.push(`Distracteur en double avec une réponse : "${conflicting[0]}".`);
    }

    if (mode === 'drag') {
        const totalPropositions = answerWords.length + distracteurs.length;
        if (totalPropositions < 2) {
            errors.push('Il faut au moins 2 propositions (réponses + distracteurs).');
        }
    }

    return errors;
};

// ─── Composant ───

const EMPTY_DISTRACTEURS = [];

const TexteTrouEditor = memo(function TexteTrouEditor({ data, onDataChange, onValidChange }) {
    const phrase = data?.phrase || '';
    const mode = data?.mode || 'drag';
    const distracteurs = data?.distracteurs || EMPTY_DISTRACTEURS;
    const strict = data?.strict || false;

    const [addError, setAddError] = useState(null);

    const answerWords = useMemo(() => extractAnswerWords(phrase), [phrase]);
    const errors = useMemo(() => validateTexteTrou(phrase, distracteurs, mode), [phrase, distracteurs, mode]);
    const isValid = errors.length === 0 && phrase.length > 0;

    useEffect(() => {
        onValidChange(isValid);
    }, [isValid, onValidChange]);

    const handlePhraseChange = useCallback((e) => {
        onDataChange(prev => ({ ...prev, phrase: e.target.value }));
    }, [onDataChange]);

    const handleModeChange = useCallback((newMode) => {
        onDataChange(prev => ({ ...prev, mode: newMode }));
    }, [onDataChange]);

    const handleStrictChange = useCallback((val) => {
        onDataChange(prev => ({ ...prev, strict: val }));
    }, [onDataChange]);

    const addDistracteur = useCallback((word) => {
        const trimmed = word.trim();
        if (!trimmed) return false;

        const normalized = normalize(trimmed);

        const currentAnswers = extractAnswerWords(data?.phrase || '');
        if (currentAnswers.some(a => normalize(a) === normalized)) {
            setAddError(`"${trimmed}" est déjà un mot-réponse.`);
            return false;
        }

        if ((data?.distracteurs || []).some(d => normalize(d) === normalized)) {
            setAddError(`"${trimmed}" est déjà dans les distracteurs.`);
            return false;
        }

        setAddError(null);
        onDataChange(prev => ({
            ...prev,
            distracteurs: [...(prev?.distracteurs || []), trimmed],
        }));
        return true;
    }, [onDataChange, data?.phrase, data?.distracteurs]);

    const removeDistracteur = useCallback((index) => {
        setAddError(null);
        onDataChange(prev => ({
            ...prev,
            distracteurs: (prev?.distracteurs || []).filter((_, i) => i !== index),
        }));
    }, [onDataChange]);

    const handleAddInput = useCallback((input) => {
        if (!input?.value?.trim()) return;
        const ok = addDistracteur(input.value);
        if (ok) input.value = '';
    }, [addDistracteur]);

    return (
        <div className="space-y-3">
            <div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mb-1.5">
                    <span className="text-[10px] sm:text-[11px] font-medium text-gray-500 uppercase tracking-wide">Phrase à trous</span>
                    <div className="flex rounded-full overflow-hidden border border-gray-200">
                        <button
                            type="button"
                            onClick={() => handleModeChange('drag')}
                            className={`px-3 py-1 text-[11px] font-medium transition-all ${
                                mode === 'drag' ? 'bg-blue-900 text-white' : 'text-gray-500'
                            }`}
                        >
                            Glisser
                        </button>
                        <button
                            type="button"
                            onClick={() => handleModeChange('saisie')}
                            className={`px-3 py-1 text-[11px] font-medium transition-all ${
                                mode === 'saisie' ? 'bg-blue-900 text-white' : 'text-gray-500'
                            }`}
                        >
                            Saisir
                        </button>
                    </div>
                </div>
                <textarea
                    value={phrase}
                    onChange={handlePhraseChange}
                    placeholder="Un triangle qui a {{trois}} côtés égaux est un triangle {{équilatéral}}."
                    rows={2}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-blue-900 resize-none"
                />
                <div className="flex items-center justify-between mt-1">
                    <p className="text-[11px] text-gray-400">
                        Entourez les mots-réponses avec {"{{"}double accolades{"}}"}.
                    </p>
                    {answerWords.length > 0 && (
                        <span className="text-[10px] font-medium text-emerald-600">
                            {answerWords.length} trou{answerWords.length > 1 ? 's' : ''} détecté{answerWords.length > 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                {answerWords.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {answerWords.map((word, i) => (
                            <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                {word}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {mode === 'drag' ? (
                <div>
                    <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Distracteurs</span>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {distracteurs.map((word, i) => (
                            <span key={i} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-900 border border-blue-200">
                                {word}
                                <button
                                    type="button"
                                    onClick={() => removeDistracteur(i)}
                                    className="text-gray-400 hover:text-red-500 transition"
                                >
                                    <X size={10} />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                        <input
                            type="text"
                            placeholder="Ajouter un distracteur..."
                            className="flex-1 sm:max-w-[200px] px-2 py-1 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:border-blue-400 placeholder-gray-400"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddInput(e.target);
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={(e) => handleAddInput(e.currentTarget.previousElementSibling)}
                            className="w-6 h-6 rounded-md flex items-center justify-center bg-blue-50 text-blue-600 hover:bg-blue-100 transition flex-shrink-0"
                        >
                            <Plus size={12} />
                        </button>
                    </div>
                    {addError && (
                        <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                            <AlertTriangle size={11} className="flex-shrink-0" />
                            {addError}
                        </p>
                    )}
                    <p className="text-[11px] text-gray-400 mt-1">
                        Mots supplémentaires pour tromper l'élève. Les réponses (vert) sont extraites de la phrase.
                    </p>
                </div>
            ) : (
                <div className="flex items-center justify-end">
                    <div className="flex rounded-full overflow-hidden border border-gray-200">
                        <button
                            type="button"
                            onClick={() => handleStrictChange(false)}
                            className={`px-3 py-1 text-[11px] font-medium transition-all ${
                                !strict ? 'bg-blue-900 text-white' : 'text-gray-500'
                            }`}
                        >
                            Tolérant
                        </button>
                        <button
                            type="button"
                            onClick={() => handleStrictChange(true)}
                            className={`px-3 py-1 text-[11px] font-medium transition-all ${
                                strict ? 'bg-blue-900 text-white' : 'text-gray-500'
                            }`}
                        >
                            Strict
                        </button>
                    </div>
                </div>
            )}

            {errors.length > 0 && phrase.length > 0 && (
                <div className="space-y-1">
                    {errors.map((err, i) => (
                        <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertTriangle size={11} className="text-amber-500 flex-shrink-0" />
                            <p className="text-[11px] text-amber-700">{err}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default TexteTrouEditor;
